import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';
import {
	DragDropContext,
	Droppable,
	DropResult,
	DroppableProvided,
} from 'react-beautiful-dnd';

import { StoreState } from '../../../../reducers';
import { Label, Button } from '../styles';
import { SlidesList, ButtonEditText } from './styles';
import SlideSelector from './SlideSelector';
import TextEditor from './Slide/TextEditor';
import ResourceSelector from '../../../../containers/ResourceSelector';
import { ResourceSelectorTheme } from '../../../ResourceSelector';
import YoutubeLinkPanel from '../YoutubeLinkPanel';
import { EditableSliderData } from '../../../../containers/Editor/actions';
import { getAspDriveItem } from '../../../../containers/DriveItemList/actions';
import { removeFileFromImport } from '../../../../containers/ImportManager/actions';
import { SlideData, ImportingSlideData, SlideTextData } from '../../../../containers/Editor/utils/indesignEditor/editableSliders/getData';
import { getDependencyString, getMediaUrl, getSliderPreviewUrl } from '../../../../containers/Editor/utils/helpers';
import { Api } from '../../../../typings/api';
import { DndItemTypes } from '../../../../typings/dnd';
import DriveItemUtils from '../../../../utils/driveItem';

interface SliderManagerProps {
	page: number;
	id: string;
	idInConfigs: string;
	slides: SlideData[];
	importingSlides: ImportingSlideData[];
	deps: string[];
	SlideComponent: React.ComponentClass<any>; // tslint:disable-line no-any
	sliderWidth: number;
	sliderHeight: number;
	shouldScaleImage: boolean;
	isBackgroundSlider?: boolean;
	config: Api.Config;
	dispatch: Dispatch<StoreState>;
	onImportSlides: (
		page: number,
		sliderId: string,
		sliderIdInConfigs: string,
		newSlidesData: ImportingSlideData[],
		isFromDesktop?: boolean,
	) => void;
	onUpdateSliderConfig: (
		page: number,
		id: string,
		data: SlideData[],
		isFirstSlideChanged?: boolean,
	) => void;
	onUpdateSliderElement: (
		page: number,
		id: string,
		data: EditableSliderData,
		shouldSave?: boolean,
		isFirstSlideChanged?: boolean,
	) => void;
}

interface RawSlideData {
	selectedItem?: Api.AspDriveResourceItem;
	url?: string;
}

enum SelectorType {
	ADD = 'ADD',
	EDIT = 'EDIT',
}

const fileExtensions = [
	...DriveItemUtils.imageWithPdfExtensions,
	...DriveItemUtils.videoExtensions,
];

const isValidResourceFromApp = ({ type }: Api.DriveItem): boolean => (
	[Api.DriveItemType.Image, Api.DriveItemType.Movie].includes(type)
);

const getSlideData = ({ selectedItem, url }: RawSlideData, slideId: string, slideText?: SlideTextData): SlideData => {
	let slideData: SlideData = {
		globalId: null,
		label: '',
		genericItemId: null,
		parentDriveGId: null,
		relUrl: '',
		externalRelUrl: '',
		isExternalLink: false,
		type: Api.AssetItemFileTypes.DEFAULT,
		id: slideId,
		slideText,
	};

	if (selectedItem) {
		const { type, label, relUrl, genericItemId, globalId, resolution, iconName } = selectedItem;
		slideData = {
			...slideData,
			type,
			label,
			relUrl: getMediaUrl(relUrl),
			genericItemId,
			globalId,
			resolution,
			iconName,
			isExternalLink: false,
			externalRelUrl: '',
		};
	}

	if (url) {
		slideData = {
			...slideData,
			externalRelUrl: url,
			isExternalLink: true,
		};
	}

	return slideData;
};

const SliderManager = ({
	page,
	id,
	idInConfigs,
	slides,
	importingSlides,
	deps,
	SlideComponent,
	sliderWidth,
	sliderHeight,
	shouldScaleImage,
	isBackgroundSlider,
	config,
	dispatch,
	onImportSlides,
	onUpdateSliderConfig,
	onUpdateSliderElement,
}: SliderManagerProps) => {
	const [selectorType, setSelectorType] = React.useState<SelectorType | null>(null);
	const [activeSlideIndex, setActiveSlideIndex] = React.useState<number | null>(null);
	const [isTextEditorDisplayed, setIsTextEditorDisplayed] = React.useState(false);
	const lastSlideElement = React.useRef<HTMLLIElement>(null);

	const getNewSlideId = (index: number = 0): string => {
		return `${Date.now()}-${slides.length + importingSlides.length + index}`;
	};

	const handleAddButtonClick = () => {
		setSelectorType(SelectorType.ADD);
	};

	const handleEditButtonClick = (index: number) => {
		setActiveSlideIndex(index);
		setSelectorType(SelectorType.EDIT);
	};

	const handleButtonEditTextClick = () => {
		setIsTextEditorDisplayed(true);
		setSelectorType(null);
	};

	const handleTextEditorClose = () => {
		setIsTextEditorDisplayed(false);
		setActiveSlideIndex(null);
	};

	const handleAddSlidesFromDesktopFiles = (files: File[]) => {
		setSelectorType(null);

		const newSlidesData: ImportingSlideData[] = files.map((file, index) => ({
			...getSlideData({}, getNewSlideId(index)),
			label: file.name,
			file,
		}));

		onImportSlides(page, id, idInConfigs, newSlidesData, true);
		scrollToLastSlide();
	};

	const handleAddSlideFromLink = (url: string) => {
		setSelectorType(null);

		const slideData = getSlideData({ url }, getNewSlideId());
		const prevSlides = [...slides];
		const isFirstSlideChanged = prevSlides.length === 0;

		onUpdateSliderConfig(page, idInConfigs, [...prevSlides, slideData], isFirstSlideChanged);

		if (isFirstSlideChanged) {
			// is needed for thumbnail regeneration
			onUpdateSliderElement(page, id, {}, true, true);
		}

		scrollToLastSlide();
	};

	const handleEditSlide = ({ selectedItem, url }: RawSlideData) => {
		setSelectorType(null);

		const slideIndex = activeSlideIndex as number;
		const { id: slideId, globalId: oldGlobalId, slideText } = slides[slideIndex];
		const slideData = getSlideData({ selectedItem, url }, slideId, slideText);
		const { globalId: newGlobalId } = slideData;
		const newSlides = [
			...slides.slice(0, slideIndex),
			slideData,
			...slides.slice(slideIndex + 1),
		];
		const isFirstSlideChanged = slideIndex === 0;

		onUpdateSliderConfig(page, idInConfigs, newSlides, isFirstSlideChanged);

		let newDeps = [...deps];

		if (oldGlobalId) {
			newDeps = newDeps.filter(dep => !dep.includes(oldGlobalId.toString()));
		}

		if (newGlobalId) {
			newDeps.push(getDependencyString(newGlobalId));
		}

		onUpdateSliderElement(page, id, {
			deps: newDeps,
		}, true, isFirstSlideChanged);

		setActiveSlideIndex(null);
	};

	const handleEditSlideText = (slideText: SlideTextData) => {
		const slideIndex = activeSlideIndex as number;
		const newSlides = [
			...slides.slice(0, slideIndex),
			{
				...slides[slideIndex],
				slideText,
			},
			...slides.slice(slideIndex + 1),
		];
		onUpdateSliderConfig(page, idInConfigs, newSlides);
	};

	const handleRemoveSlide = (slideId: string, index: number) => {
		const isImportingSlide = importingSlides.map(({ id }) => id).includes(slideId);

		if (isImportingSlide) {
			onUpdateSliderElement(page, id, {
				importingSlides: importingSlides.filter(({ id }) => id !== slideId),
			}, false);
			dispatch(removeFileFromImport(slideId));
			return;
		}

		const { globalId } = slides[index];
		const newSlides = slides.filter((slide, slideIndex) => slideIndex !== index);
		const isFirstSlideChanged = index === 0;

		onUpdateSliderConfig(page, idInConfigs, newSlides, isFirstSlideChanged);
		onUpdateSliderElement(page, id, {
			deps: globalId ? deps.filter(dep => !dep.includes(globalId.toString())) : deps,
		}, true, isFirstSlideChanged);
	};

	const handleDragEndSlide = ({ source, destination }: DropResult) => {
		if (!destination || source.index === destination.index) {
			return;
		}

		const slideData = slides[source.index];
		let newSlides = slides.filter((slide, slideIndex) => slideIndex !== source.index);
		newSlides = [
			...newSlides.slice(0, destination.index),
			slideData,
			...newSlides.slice(destination.index),
		];
		const isFirstSlideChanged = source.index === 0 || destination.index === 0;

		onUpdateSliderConfig(page, idInConfigs, newSlides, isFirstSlideChanged);

		if (isFirstSlideChanged) {
			// is needed for thumbnail regeneration
			onUpdateSliderElement(page, id, {}, true, true);
		}
	};

	const handleResourceSelect = async ([driveItem]: Api.DriveItem[]): Promise<void> => {
		setSelectorType(null);
		const aspDriveItem = await dispatch(getAspDriveItem(driveItem.id));
		handleEditSlide({ selectedItem: aspDriveItem });
	};

	const handleMultipleResourcesSelect = (selectedItems: Api.DriveItem[]) => {
		setSelectorType(null);

		const newSlidesData: ImportingSlideData[] = selectedItems.map(({ label, id }, index) => ({
			...getSlideData({}, getNewSlideId(index)),
			label,
			globalId: id,
		}));

		onImportSlides(page, id, idInConfigs, newSlidesData);
		scrollToLastSlide();
	};

	const handleLinkChange = (url: string) => {
		if (selectorType === SelectorType.ADD) {
			handleAddSlideFromLink(url);
		}

		if (selectorType === SelectorType.EDIT) {
			handleEditSlide({ url });
		}
	};

	const scrollToLastSlide = () => {
		// is needed for waiting when importing slides are added to the list
		setTimeout(() => {
			if (lastSlideElement.current) {
				lastSlideElement.current.scrollIntoView();
			}
		}, 0);
	};

	const renderSlideSelector = () => {
		if (selectorType === SelectorType.ADD) {
			return (
				<SlideSelector
					label="messages.add_slide"
					onCancel={() => setSelectorType(null)}
				>
					<ResourceSelector
						id="add-slide"
						isValidResourceFromApp={isValidResourceFromApp}
						fileExtensions={fileExtensions}
						isFileExtensionsDisplayed={true}
						isOptimizedImageImport={true}
						isPublicationDependency={true}
						isMultipleSelection={true}
						linkPanel={(
							<YoutubeLinkPanel
								value=""
								onChange={handleLinkChange}
							/>
						)}
						theme={ResourceSelectorTheme.DARK}
						customImportHandler={files => handleAddSlidesFromDesktopFiles(files as File[])}
						onSelect={handleMultipleResourcesSelect}
					/>
				</SlideSelector>
			);
		}

		if (selectorType === SelectorType.EDIT) {
			const {
				id: slideId,
				externalRelUrl,
				slideText,
			} = slides[activeSlideIndex as number];

			return (
				<SlideSelector
					label="messages.modify_slide"
					onCancel={() => setSelectorType(null)}
				>
					<ResourceSelector
						id={slideId}
						isValidResourceFromApp={isValidResourceFromApp}
						fileExtensions={fileExtensions}
						isFileExtensionsDisplayed={true}
						isOptimizedImageImport={true}
						isPublicationDependency={true}
						linkPanel={(
							<YoutubeLinkPanel
								value={externalRelUrl}
								onChange={handleLinkChange}
							/>
						)}
						theme={ResourceSelectorTheme.DARK}
						onSelect={handleResourceSelect}
					/>
					{slideText && slideText.value &&
						<ButtonEditText onClick={handleButtonEditTextClick}>
							<Translate value="messages.edit_text" />
						</ButtonEditText>
					}
				</SlideSelector>
			);
		}

		return null;
	};

	if (isTextEditorDisplayed) {
		const {
			relUrl,
			externalRelUrl,
			isExternalLink,
			iconName,
			slideText,
		} = slides[activeSlideIndex as number];

		return (
			<TextEditor
				previewUrl={getSliderPreviewUrl(config, relUrl, isExternalLink, externalRelUrl, iconName)}
				previewWidth={sliderWidth}
				previewHeight={sliderHeight}
				shouldScalePreview={shouldScaleImage}
				isBackgroundSlide={isBackgroundSlider}
				text={slideText as SlideTextData}
				slidesLength={slides.length}
				onConfirm={handleEditSlideText}
				onCancel={handleTextEditorClose}
			/>
		);
	}

	if (selectorType) {
		return renderSlideSelector();
	}

	return (
		<>
			<Label value="messages.create_your_slider" />
			<Button
				disabled={importingSlides.length > 0}
				onClick={handleAddButtonClick}
			>
				<Translate value="messages.add_slide" />
			</Button>
			<DragDropContext onDragEnd={handleDragEndSlide}>
				<Droppable
					droppableId="slider"
					type={DndItemTypes.EDITABLE_SLIDE}
				>
					{(provided: DroppableProvided) => (
						<SlidesList
							ref={provided.innerRef}
							{...provided.droppableProps}
						>
							{slides.map((slide, index) => (
								<li
									ref={index === slides.length - 1 ? lastSlideElement : undefined}
									key={index}
								>
									<SlideComponent
										page={page}
										sliderId={id}
										item={slide}
										index={index}
										onEdit={handleEditButtonClick}
										onRemove={handleRemoveSlide}
									/>
								</li>
							))}
							{importingSlides.map((slide, index) => {
								const slideIndex = slides.length + index;
								return (
									<li key={slideIndex}>
										<SlideComponent
											page={page}
											sliderId={id}
											item={slide}
											index={slideIndex}
											onEdit={handleEditButtonClick}
											onRemove={handleRemoveSlide}
										/>
									</li>
								);
							})}
							{provided.placeholder}
						</SlidesList>
					)}
				</Droppable>
			</DragDropContext>
		</>
	);
};

export default SliderManager;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/SliderManager/index.tsx